/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {translate} from '@ion/core/src/locale/locale';
import {EntityStatus, SentinelDevices} from '@ion/core/src/types/entities';
import SearchField from '@ion/dls/src/components/search/SearchField';
import TopNavSearchIcon from '@ion/dls/src/icons/TopNavSearchIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import SentinelDevicesTable from './SentinelDevicesTable';

export interface SentinelFormProps {
  devicesRef?: any;
  devices: SentinelDevices;
  serviceStatus: EntityStatus;
  identifier?: string;
  accountId?: number;
  error?: boolean;
  isSelectedFromGrid?: boolean;
}

const useStyles = makeStyles(() => ({
  searchIcon : {
    padding        : '12px 0 12px 0px',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  endAdornment : {
    cursor               : 'pointer',
    paddingTop           : 10,
    '& .MuiSvgIcon-root' : {
      width  : '0.65em',
      height : '0.65em'
    }
  },
  errorMessageContainer : {
    padding : '0 0 10px 0'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  disabledContainer : {
    opacity       : 0.5,
    pointerEvents : 'none'
  }
}));

const searchFieldStyles = () => ({
  searchFieldRoot : {
    width  : '48% !important',
    height : 50
  },
  searchFieldInput : {
    fontSize : '16px'
  }
});

/**
 * The SentinelForm component provides a Search field for searching Primary devices along with table 
 * of primary and secondary devices.
 *
 * ```typescript
 *   import SentinelForm from '@ion/applications/src/bulk/SentinelForm';
 *
 *   <SentinelForm devices={devices} ... />
 * ```
 */
export const SentinelForm = ({
  devices,
  devicesRef,
  accountId,
  serviceStatus,
  error = false,
  identifier = 'esn',
  isSelectedFromGrid = false
}: SentinelFormProps) => {
  const classes                           = useStyles();
  const [searchValue, setSearchValue]     = useState('');
  const [pairedDevices, setPairedDevices] = useState(devices);

  useEffect(() => {
    setPairedDevices(devices);
  }, [devices]);

  const onSearchChange = function({target}) {
    setSearchValue(target.value);
    setPairedDevices(devicesRef?.current?.filter(device => device?.primaryDevice?.includes(target.value)));
  };

  const removeSearch = function() {
    setSearchValue('');
    setPairedDevices(devicesRef?.current || devices);
  };

  return (
    <div className={serviceStatus===EntityStatus.Disabled ? classes.disabledContainer: ''}>
      <SearchField overrides={searchFieldStyles}
        disabled={serviceStatus===EntityStatus.Disabled}
        onChange={onSearchChange}
        optionsProps={{
          value : searchValue
        }}
        startAdornment={(
          <div className={classes.searchIcon}>
            <TopNavSearchIcon />
          </div>
        )}
        endAdornment={searchValue
          ? (
            <div className={classes.endAdornment} onClick={removeSearch}>
              <CloseIcon />
            </div>
          ) : null}
        placeholder={translate('common:editors.bulkSentinel.searchPlaceholder')} />
      
      <Render.If condition={error}>
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{translate('common:editors.bulkSentinel.requiredMsg')}</span>
        </div>
      </Render.If>
            
      <SentinelDevicesTable devices={pairedDevices}
        devicesRef={devicesRef}
        serviceStatus={serviceStatus}
        identifier={isSelectedFromGrid ? 'esn' : identifier}
        accountId={accountId} />
    </div>
  );
};

export default SentinelForm;
